// apps/web/src/pages/author/widget-config/summary-checklist-config.tsx
//
// Phase 8 config sub-form for template `summary-checklist` — drives the inline
// configSchema editor per authr-05. Field set mirrors
// packages/shared/src/schemas/widget-templates/summary-checklist.ts AND the
// per-template walker at packages/shared/src/forms/per-template/summary-checklist.ts
// (Plan 03).
//
// Sub-form fields use per-path useWatch only — NEVER top-level (RESEARCH
// §"Pattern 3"). No formState reads. Every useFieldArray map uses field.id
// keys per authr-03 (Plan 01 grep gate enforces).
//
// configSchema shape (verbatim):
//   items: ReadonlyArray<SummaryChecklistItem{ id; title; description?; subItems?;
//                                              groupId?; visibleWhen? }>
//   source: { type: 'widget' | 'selector' | 'static'; sourceWidgetId?; pathInState? }
//   viewMode: 'title' | 'snippet' | 'full' | 'expanded'
//   groupBy: 'category' | 'none'
//
// W11 — exports `defaultSummaryChecklistConfig(): SummaryChecklistConfig` factory.
//
// Note: useFormContext().setValue is used for toggling the optional
// `visibleWhen` subtree on/off — this is a structural state mutation (subtree
// presence) rather than a per-leaf edit, so the local setValue call is the
// correct vehicle. We do NOT read formState anywhere in this file.

import type { SummaryChecklistConfig } from "@the-recipe-spread/shared";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState } from "@/components/ui/empty-state";
import { Fieldset } from "@/components/ui/fieldset";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FieldArrayRow } from "../FieldArrayRow";

export interface SummaryChecklistConfigSubFormProps {
  instanceId: string;
  basePath: string;
}

// W11 — minimum-valid SummaryChecklistConfig per the configSchema.
// `source.type: 'static'` is the lightest source variant (no sibling required).
export function defaultSummaryChecklistConfig(): SummaryChecklistConfig {
  return {
    items: [{ id: "item-1", title: "Item 1" }],
    source: { type: "static" },
    viewMode: "title",
    groupBy: "none",
  };
}

const SOURCE_TYPES = ["widget", "selector", "static"] as const;
const VIEW_MODES = ["title", "snippet", "full", "expanded"] as const;
const GROUP_BY_OPTIONS = ["category", "none"] as const;

export function SummaryChecklistConfigSubForm({
  instanceId: _instanceId,
  basePath,
}: SummaryChecklistConfigSubFormProps): React.ReactElement {
  const { control } = useFormContext();
  const itemsArray = useFieldArray({
    control,
    name: `${basePath}.items`,
  });

  // Per-path useWatch — narrow subscription to the source discriminator.
  // No top-level useWatch (Pitfall 1 + 2 hygiene).
  const sourceType = useWatch({
    control,
    name: `${basePath}.source.type`,
  }) as string | undefined;

  return (
    <div className="flex flex-col gap-4">
      <Fieldset legend="SOURCE">
        <FormField
          control={control}
          name={`${basePath}.source.type`}
          render={({ field: f }) => (
            <FormItem data-field-path={`${basePath}.source.type`}>
              <FormLabel>Source type</FormLabel>
              <Select value={(f.value as string) ?? "static"} onValueChange={f.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="static" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {SOURCE_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        {sourceType === "widget" ? (
          <FormField
            control={control}
            name={`${basePath}.source.sourceWidgetId`}
            render={({ field: f }) => (
              <FormItem data-field-path={`${basePath}.source.sourceWidgetId`}>
                <FormLabel>Source widget id</FormLabel>
                <FormControl>
                  <Input {...f} value={f.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}
        {sourceType === "selector" ? (
          <FormField
            control={control}
            name={`${basePath}.source.pathInState`}
            render={({ field: f }) => (
              <FormItem data-field-path={`${basePath}.source.pathInState`}>
                <FormLabel>Path in state</FormLabel>
                <FormControl>
                  <Input {...f} value={f.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}
      </Fieldset>

      <Fieldset legend="ITEMS">
        {itemsArray.fields.length === 0 ? (
          <EmptyState
            title="No items yet."
            cta={{
              label: "Add item",
              onClick: () =>
                itemsArray.append({
                  id: `item-${itemsArray.fields.length + 1}`,
                  title: `Item ${itemsArray.fields.length + 1}`,
                }),
            }}
          />
        ) : (
          <>
            {itemsArray.fields.map((field, index) => (
              <ChecklistItemRow
                key={field.id}
                index={index}
                basePath={basePath}
                canMoveUp={index > 0}
                canMoveDown={index < itemsArray.fields.length - 1}
                onMoveUp={() => itemsArray.move(index, index - 1)}
                onMoveDown={() => itemsArray.move(index, index + 1)}
                onRemove={() => itemsArray.remove(index)}
              />
            ))}
            <button
              type="button"
              onClick={() =>
                itemsArray.append({
                  id: `item-${itemsArray.fields.length + 1}`,
                  title: `Item ${itemsArray.fields.length + 1}`,
                })
              }
              className="self-start text-sm text-muted-foreground hover:text-foreground underline underline-offset-2"
            >
              Add item
            </button>
          </>
        )}
      </Fieldset>

      <FormField
        control={control}
        name={`${basePath}.viewMode`}
        render={({ field: f }) => (
          <FormItem data-field-path={`${basePath}.viewMode`}>
            <FormLabel>View mode</FormLabel>
            <Select value={(f.value as string) ?? "title"} onValueChange={f.onChange}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="title" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {VIEW_MODES.map((v) => (
                  <SelectItem key={v} value={v}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`${basePath}.groupBy`}
        render={({ field: f }) => (
          <FormItem data-field-path={`${basePath}.groupBy`}>
            <FormLabel>Group by</FormLabel>
            <Select value={(f.value as string) ?? "none"} onValueChange={f.onChange}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="none" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {GROUP_BY_OPTIONS.map((g) => (
                  <SelectItem key={g} value={g}>
                    {g}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
SummaryChecklistConfigSubForm.displayName = "SummaryChecklistConfigSubForm";

interface ChecklistItemRowProps {
  index: number;
  basePath: string;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

function ChecklistItemRow({
  index,
  basePath,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onRemove,
}: ChecklistItemRowProps): React.ReactElement {
  const { control, setValue } = useFormContext();
  // Per-path useWatch — narrow subscription to the visibleWhen presence.
  const visibleWhen = useWatch({
    control,
    name: `${basePath}.items.${index}.visibleWhen`,
  }) as { sourceWidgetId?: string; anyOf?: ReadonlyArray<string> } | undefined;
  const visibleWhenEnabled = visibleWhen !== undefined;

  return (
    <FieldArrayRow
      singular="item"
      index={index}
      canMoveUp={canMoveUp}
      canMoveDown={canMoveDown}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
      onRemove={onRemove}
      deleteDialogTitle="Delete item?"
      deleteDialogBody="This item will be removed from the checklist."
      deleteConfirmLabel="Delete item"
    >
      <FormField
        control={control}
        name={`${basePath}.items.${index}.id`}
        render={({ field: f }) => (
          <FormItem data-field-path={`${basePath}.items.${index}.id`}>
            <FormLabel>Id (kebab-case)</FormLabel>
            <FormControl>
              <Input {...f} value={f.value ?? ""} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`${basePath}.items.${index}.title`}
        render={({ field: f }) => (
          <FormItem data-field-path={`${basePath}.items.${index}.title`}>
            <FormLabel>Title</FormLabel>
            <FormControl>
              <Input {...f} value={f.value ?? ""} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="flex items-center gap-2">
        <Checkbox
          id={`${basePath}.items.${index}.visibleWhen-toggle`}
          checked={visibleWhenEnabled}
          onCheckedChange={(checked) => {
            const isChecked = checked === true;
            // Structural toggle: enable creates a minimum-valid VisibleWhen;
            // disable clears the subtree.
            setValue(
              `${basePath}.items.${index}.visibleWhen`,
              isChecked ? { sourceWidgetId: "", anyOf: [""] } : undefined,
              { shouldDirty: true, shouldTouch: true },
            );
          }}
        />
        <label
          htmlFor={`${basePath}.items.${index}.visibleWhen-toggle`}
          className="text-sm text-foreground"
        >
          Show only when (visibleWhen)
        </label>
      </div>
      {visibleWhenEnabled ? (
        <Fieldset legend="VISIBLE WHEN">
          <FormField
            control={control}
            name={`${basePath}.items.${index}.visibleWhen.sourceWidgetId`}
            render={({ field: f }) => (
              <FormItem data-field-path={`${basePath}.items.${index}.visibleWhen.sourceWidgetId`}>
                <FormLabel>Source widget id</FormLabel>
                <FormControl>
                  <Input {...f} value={f.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={`${basePath}.items.${index}.visibleWhen.anyOf`}
            render={({ field: f }) => (
              <FormItem data-field-path={`${basePath}.items.${index}.visibleWhen.anyOf`}>
                <FormLabel>Any of (comma-separated option ids)</FormLabel>
                <FormControl>
                  <Textarea
                    value={Array.isArray(f.value) ? (f.value as string[]).join(", ") : ""}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const parts = raw
                        .split(",")
                        .map((s) => s.trim())
                        .filter((s) => s.length > 0);
                      f.onChange(parts.length === 0 ? [""] : parts);
                    }}
                    onBlur={f.onBlur}
                    name={f.name}
                    ref={f.ref}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Fieldset>
      ) : null}
    </FieldArrayRow>
  );
}
